import { useCallback, useEffect, useState, useMemo } from 'react';
import { Flex, TextStyled } from '../../../component-library';
import { PatientStimulusPoint, FixationTargetVideo } from '../../../components';
import { useTestContext } from '../../../context/testContext';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useStimulusPosition, useTestUpdate, useNewStimulus } from '../../../helpers/forth-valley-hooks';
import useNextStimulusCountdown from '../../../helpers/forth-valley-hooks/useNextStimulusCountdown';
import { LocalVideo, RemoteVideo } from '../../../2022-component-library';

const VisualFieldForthValleyTest = () => {
  const { testData } = useTestContext();
  const messageFromOptician = useReceiveMessage();
  const postMessage = usePostMessage();
  const { testProgressData, updateTestProgressData, batchMultipleUpdates } = useTestUpdate();
  const getNextStimulus = useNewStimulus();
  const calculateStimulusPosition = useStimulusPosition();
  const { showStimulus, readyForNextStimulus } = useNextStimulusCountdown();
  const [visualFieldTestData, setVisualFieldTestData] = useState({});
  const [stimulusCoordinates, setStimulusCoordinates] = useState(null);
  const [currentDot, setCurrentDot] = useState(null);
  const [fixationTargetInPosition, setFixationTargetInPosition] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [recordingAcuityResults, setRecordingAcuityResults] = useState(true);

  const boundaries = useMemo(() => {
    return {
      x: window.screen.availWidth,
      y: window.screen.availHeight,
    };
  }, []);

  const centerScreen = useMemo(() => {
    return {
      x: boundaries['x'] / 2,
      y: boundaries['y'] / 2,
    };
  }, [boundaries]);

  const fixationSize = useMemo(() => {
    return {
      x: testData?.visualFieldSettings?.fixationTargetDimensions?.width,
      y: testData?.visualFieldSettings?.fixationTargetDimensions?.height,
    };
  }, [testData?.visualFieldSettings?.fixationTargetDimensions]);

  const [fixationTargetCoordinates, setFixationTargetCoordinates] = useState({
    x: centerScreen['x'] - fixationSize['x'] / 2,
    y: centerScreen['y'] - fixationSize['y'] / 2,
  });

  const getStimulusPosition = useCallback(() => {
    const { stimulusPointX, stimulusPointY, fixationTargetX, fixationTargetY } = calculateStimulusPosition(currentDot, fixationTargetCoordinates);

    setStimulusCoordinates({ x: stimulusPointX, y: stimulusPointY });

    if (fixationTargetX) {
      setFixationTargetCoordinates((existingCoordinates) => {
        return { ...existingCoordinates, x: fixationTargetX };
      });
      setFixationTargetInPosition(false);
    }

    if (fixationTargetY) {
      setFixationTargetCoordinates((existingCoordinates) => {
        return { ...existingCoordinates, y: fixationTargetY };
      });
      setFixationTargetInPosition(false);
    }

    if (!fixationTargetX && !fixationTargetY) {
      setFixationTargetInPosition(true);
    }
  }, [currentDot, fixationTargetCoordinates, calculateStimulusPosition]);

  const isEyeBlindSpot = useCallback(
    (currentDot) => {
      if (currentDot['blindspot']) {
        if (visualFieldTestData['eyeTested'] !== 'bothEyes') {
          if (
            visualFieldTestData['eyeTested'] === 'leftEye' &&
            (currentDot['orientation'] === 'topLeft' || currentDot['orientation'] === 'bottomLeft')
          ) {
            return true;
          } else if (
            visualFieldTestData['eyeTested'] === 'rightEye' &&
            (currentDot['orientation'] === 'topRight' || currentDot['orientation'] === 'bottomRight')
          ) {
            return true;
          }
        }
      }

      return false;
    },
    [visualFieldTestData],
  );

  const markDotSeen = useCallback(
    (orientationResponse) => {
      // if (isEyeBlindSpot(currentDot)) {
      //   return batchMultipleUpdates({
      //     incrementBlindSpotCount: true,
      //     incrementBlindSpotSeenCount: true,
      //     updateCurrentLandoltCQuadrant: null,
      //     updatePatientQuadrantResponse: null,
      //   });
      // }

      if (currentDot['repeat']) {
        return batchMultipleUpdates({
          updateCurrentLandoltCQuadrant: null,
          updatePatientQuadrantResponse: null,
          removeFromSeenDotArray: currentDot,
          addSeenDot: { ...currentDot, seenTwice: true },
        });
      }

      const indexOfAlreadyMissedDot = testProgressData.missedOnceDots.findIndex((dot) => {
        return dot?.id === currentDot?.id;
      });

      if (indexOfAlreadyMissedDot !== -1) {
        updateTestProgressData({ type: 'removeFromMissedOnceArray', payload: indexOfAlreadyMissedDot });
      }

      if (orientationResponse === 'correct') {
        if (recordingAcuityResults) updateTestProgressData({ type: 'updateLogmarSize', payload: -0.1 });
        updateTestProgressData({ type: 'updateOrientationCorrect', payload: { ...currentDot, seen: true } });
      } else {
        //below code was written in a deadline rush. improve/pull out to custom hook.
        if (testProgressData.failedLogmarSizes.includes(testProgressData.logmarSize.toFixed(1))) {
          setRecordingAcuityResults(false);
          const finalLogmarSize = testProgressData.logmarSize >= 0.8 ? 1.0 : (testProgressData.logmarSize * 10 + 2) * 0.1;
          updateTestProgressData({ type: 'setFinalLogmarSize', payload: finalLogmarSize });
        } else if (recordingAcuityResults) {
          batchMultipleUpdates({ updateLogmarSize: 0.1, updateFailedLogmarSizes: testProgressData.logmarSize.toFixed(1) });
        }

        updateTestProgressData({ type: 'updateOrientationIncorrect', payload: { ...currentDot, seen: true } });
      }

      if (!currentDot['repeat']) {
        batchMultipleUpdates({
          addSeenDot: { ...currentDot, seen: true, patientOrientationResponse: orientationResponse },
          updateActiveDot: { ...currentDot, seen: true, notSeenOnce: false, patientOrientationResponse: orientationResponse },
          updateCurrentLandoltCQuadrant: null,
          updatePatientQuadrantResponse: null,
        });
      }
    },
    [currentDot, isEyeBlindSpot, testProgressData.missedOnceDots, updateTestProgressData, batchMultipleUpdates],
  );

  const markPointUnseen = useCallback(() => {
    // if (isEyeBlindSpot(currentDot)) {
    //   updateTestProgressData({ incrementBlindSpotCount: true });
    //   if (currentDot['repeat'] - 1 > 0) {
    //     updateTestProgressData({ type: 'addDotForDisplay', payload: currentDot });
    //   }
    // } else {
    const indexOfAlreadyMissedDot = testProgressData.missedOnceDots?.findIndex((dot) => {
      return dot?.id === currentDot?.id;
    });

    const indexOfAlreadySeenDot = testProgressData.seenDots?.findIndex((dot) => {
      return dot?.id === currentDot?.id;
    });

    const dotWasMissedOnce = indexOfAlreadyMissedDot !== -1;
    const dotWasAlreadySeen = indexOfAlreadySeenDot !== -1;

    if (dotWasAlreadySeen) {
      updateTestProgressData({ type: 'decreaseTestConfidence' });
    } else if (dotWasMissedOnce) {
      batchMultipleUpdates({
        removeFromMissedOnceArray: indexOfAlreadyMissedDot,
        addMissedTwiceDot: currentDot,
        updateActiveDot: { ...currentDot, notSeenTwice: true },
      });
    } else {
      batchMultipleUpdates({
        addMissedOnceDot: currentDot,
        addDotForDisplay: currentDot,
        updateActiveDot: { ...currentDot, notSeenOnce: true },
      });
    }
    // }

    return batchMultipleUpdates({ updateCurrentLandoltCQuadrant: null, updatePatientQuadrantResponse: null });
  }, [batchMultipleUpdates, updateTestProgressData, testProgressData.missedOnceDots, testProgressData.seenDots, currentDot]);

  const getIbisDots = useCallback(() => {
    let localDotsForDisplay = [];
    if (visualFieldTestData) {
      const { forthValleyVisualFieldDots } = visualFieldTestData;

      forthValleyVisualFieldDots.forEach((individualDot) => {
        if (!individualDot['disabled']) {
          localDotsForDisplay.push(individualDot);
        }
      });
    }

    return localDotsForDisplay;
  }, [visualFieldTestData]);

  useEffect(() => {
    //this updates the optician when anything changes throughout the test.
    postMessage({ type: 'testProgressUpdate', content: testProgressData });
  }, [testProgressData, postMessage]);

  useEffect(() => {
    //this receives a message and either sets or wipes the data for the test.
    if (messageFromOptician.type === 'beginTest') {
      setVisualFieldTestData(messageFromOptician.content);
    } else if (messageFromOptician.type === 'stopTest') {
      setVisualFieldTestData({});
    } else if (messageFromOptician.type === 'resetTest') {
      updateTestProgressData({ type: 'reset' });
      setVisualFieldTestData({});
    }
  }, [messageFromOptician, updateTestProgressData]);

  useEffect(() => {
    //this reacts to the data set/wiped above and calls the appropriate function.
    const initialiseLocalStateValues = () => {
      setCurrentDot(null);
      setStimulusCoordinates(null);
      setFixationTargetCoordinates({ x: centerScreen['x'] - fixationSize['x'] / 2, y: centerScreen['y'] - fixationSize['y'] / 2 });
    };
    const startTest = () => {
      initialiseLocalStateValues();
      batchMultipleUpdates({ reset: true, setDotsForDisplay: getIbisDots() });
      setIsRunning(true);
    };

    const stopTest = () => {
      initialiseLocalStateValues();
      updateTestProgressData({ type: 'reset' });
      setIsRunning(false);
      setRecordingAcuityResults(true);
    };

    if (Object.keys(visualFieldTestData).length) {
      startTest();
    } else stopTest();
  }, [visualFieldTestData, batchMultipleUpdates, getIbisDots, updateTestProgressData, centerScreen, fixationSize]);

  useEffect(() => {
    //this gets a dot from the array to display. if there are none left, it ends the test.
    const { currentLandoltCQuadrant, dotsForDisplay } = testProgressData;
    const getADot = () => {
      const { newDot, newDotArray } = getNextStimulus(testProgressData);
      setCurrentDot(newDot);
      if (newDotArray) updateTestProgressData({ type: 'setDotsForDisplay', payload: newDotArray });
    };

    if (isRunning && !currentDot && !currentLandoltCQuadrant) {
      if (dotsForDisplay?.length) {
        getADot();
      } else {
        updateTestProgressData({ type: 'setTestEnded', payload: true });
        setIsRunning(false);
      }
    }
  }, [currentDot, isRunning, testProgressData, updateTestProgressData, getNextStimulus]);

  useEffect(() => {
    //this reacts to currentDot being set and retrieves a position for it.
    if (currentDot && !stimulusCoordinates) {
      getStimulusPosition();
    }
  }, [currentDot, stimulusCoordinates, getStimulusPosition]);

  useEffect(() => {
    //this shows the stimulus point, then gets the next one when ready
    if (currentDot && stimulusCoordinates && readyForNextStimulus) {
      updateTestProgressData({ type: 'updateActiveDot', payload: currentDot });
      postMessage({ type: 'nextStimulus' });

      return () => {
        setCurrentDot(null);
        setStimulusCoordinates(null);
        setFixationTargetInPosition(false);
      };
    }
  }, [currentDot, updateTestProgressData, stimulusCoordinates, readyForNextStimulus, postMessage]);

  useEffect(() => {
    //this handles the patient response to the stimulus
    if (currentDot && !showStimulus && testProgressData.currentLandoltCQuadrant) {
      if (testProgressData.patientQuadrantResponse) {
        if (testProgressData.currentLandoltCQuadrant === testProgressData.patientQuadrantResponse) {
          markDotSeen('correct');
        } else {
          markDotSeen('incorrect');
        }
      } else markPointUnseen();
    }
  }, [currentDot, showStimulus, markDotSeen, testProgressData.currentLandoltCQuadrant, testProgressData.patientQuadrantResponse, markPointUnseen]);

  useEffect(() => {
    //this waits for any transition to complete, then confirms that the fixation target is in position. previously handled by onTransitionEnd event which was causing bugs.
    if (!fixationTargetInPosition && isRunning) {
      const reportTransitionFinished = setTimeout(() => {
        return setFixationTargetInPosition(true);
      }, 2000);

      return () => clearTimeout(reportTransitionFinished);
    }
  }, [fixationTargetInPosition, isRunning]);

  return (
    <Flex bg='white' width='100vw' height='100vh' style={{ position: 'relative', zIndex: '1', cursor: 'none' }}>
      {testProgressData.testEnded ? (
        <Flex alignItems='center' flexDirection='column' justifyContent='center' width='100%' height='100%'>
          <Flex width='50vw' marginBottom='1rem'>
            <Flex marginRight='1rem'>
              <LocalVideo />
            </Flex>
            <Flex>
              <RemoteVideo />
            </Flex>
          </Flex>
          <TextStyled textAlign='center' fontSize='36px'>
            Test Completed
          </TextStyled>
        </Flex>
      ) : (
        <>
          <FixationTargetVideo fixationTargetCoordinates={fixationTargetCoordinates} fixationSize={fixationSize} />
          {showStimulus && fixationTargetInPosition && (
            <PatientStimulusPoint
              logmarSize={testProgressData.logmarSize}
              stimulusCoordinates={stimulusCoordinates}
              updateTestProgressData={updateTestProgressData}
              patientInputMethod={testData?.visualFieldForthValley?.patientInputMethod}
            />
          )}
        </>
      )}
    </Flex>
  );
};

export default VisualFieldForthValleyTest;
